import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useWaitingVariantTracking } from '../useWaitingVariantTracking';

export type Slide = {
	emoji: string;
	title: string;
	text: string;
};

export type SlideDeck = {
	id: string;
	title: string;
	emoji: string;
	slides: Slide[];
};

const AUTO_ADVANCE_MS = 6000;

// Motor de slides educativos: reproduce un deck con auto-avance, tap para
// adelantar y un control para cambiar de tema. El contenido vive en
// content/slideDecks.ts.
const SlideDeckPlayer = ({ decks }: { decks: SlideDeck[] }) => {
	const { trackInteraction } = useWaitingVariantTracking();
	const [deckIndex, setDeckIndex] = useState(() =>
		Math.floor(Math.random() * decks.length),
	);
	const [slideIndex, setSlideIndex] = useState(0);

	const deck = decks[deckIndex];
	const slide = deck.slides[slideIndex];

	useEffect(() => {
		const timer = setInterval(
			() => setSlideIndex((prev) => (prev + 1) % deck.slides.length),
			AUTO_ADVANCE_MS,
		);
		return () => clearInterval(timer);
	}, [deck]);

	const handleAdvance = () => {
		setSlideIndex((prev) => (prev + 1) % deck.slides.length);
	};

	const handleNextDeck = () => {
		const nextDeckIndex = (deckIndex + 1) % decks.length;
		trackInteraction('slides_deck_changed', { deck: decks[nextDeckIndex].id });
		setDeckIndex(nextDeckIndex);
		setSlideIndex(0);
	};

	return (
		<div className='flex flex-col gap-3 rounded-xl bg-h-primary-95 px-4 py-5'>
			<div className='flex items-center justify-between'>
				<p className='text-[11px] font-bold uppercase tracking-[0.08em] text-h-primary-40'>
					{deck.emoji} {deck.title}
				</p>
				<p className='text-xs text-h-neutral-50'>
					{slideIndex + 1} de {deck.slides.length}
				</p>
			</div>
			<button
				type='button'
				onClick={handleAdvance}
				aria-label='Siguiente slide'
				className='flex min-h-[160px] flex-col items-center justify-center gap-2 px-2 text-center'
			>
				<AnimatePresence mode='wait'>
					<motion.div
						key={`${deckIndex}-${slideIndex}`}
						initial={{ opacity: 0, y: 12 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -12 }}
						transition={{ duration: 0.3, ease: 'easeInOut' }}
						className='flex flex-col items-center gap-2'
					>
						<span className='text-4xl'>{slide.emoji}</span>
						<h3 className='text-base font-semibold text-h-text-heading leading-snug'>
							{slide.title}
						</h3>
						<p className='text-sm text-h-neutral-50 leading-relaxed'>
							{slide.text}
						</p>
					</motion.div>
				</AnimatePresence>
			</button>
			<div className='flex items-center justify-between'>
				<div className='flex items-center'>
					{deck.slides.map((_, index) => (
						<button
							key={index}
							type='button'
							onClick={() => setSlideIndex(index)}
							aria-label={`Ir al slide ${index + 1}`}
							className='flex h-6 min-w-6 items-center justify-center'
						>
							<span
								className={`h-1.5 rounded-full transition-all duration-200 ${
									index === slideIndex
										? 'w-4 bg-h-primary-40'
										: 'w-1.5 bg-h-primary-70'
								}`}
							/>
						</button>
					))}
				</div>
				<button
					type='button'
					onClick={handleNextDeck}
					className='rounded-full bg-white px-3 py-1.5 text-sm font-medium text-h-primary-40'
				>
					Otro tema →
				</button>
			</div>
		</div>
	);
};

export default SlideDeckPlayer;
